// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
/**
 * Shared utilities for all SAPIENS calculators.
 * Import from here in each individual calculator file.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Info } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Title, Tooltip as ChartTooltip, Legend, Filler
} from 'chart.js';

// Register Chart.js components once
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Title, ChartTooltip, Legend, Filler
);

// ── Instruments for PAC Avanzato ──
export const STRUMENTI_DATA = [
  { group: 'ETF Azionari Globali', id: 'etf_world',  label: 'ETF MSCI World',           ret: 7,    risk: 0 },
  { group: 'ETF Azionari Globali', id: 'sp500',      label: 'ETF S&P 500',              ret: 10,   risk: 0 },
  { group: 'ETF Azionari Globali', id: 'nasdaq100',  label: 'ETF Nasdaq 100 ⚠',         ret: 12,   risk: 1 },
  { group: 'ETF Azionari Globali', id: 'em',         label: 'ETF MSCI Emerging Markets', ret: 6,    risk: 0 },
  { group: 'ETF Azionari Globali', id: 'ftse_aw',    label: 'ETF FTSE All-World',        ret: 7.5,  risk: 0 },
  { group: 'ETF Azionari Globali', id: 'stoxx600',   label: 'ETF Europa STOXX 600',      ret: 6,    risk: 0 },
  { group: 'ETF Obbligazionari',   id: 'btp',        label: 'BTP Italia',                ret: 3,    risk: 0 },
  { group: 'ETF Obbligazionari',   id: 'euro_gov',   label: 'ETF Euro Government Bond',  ret: 2.5,  risk: 0 },
  { group: 'ETF Obbligazionari',   id: 'global_agg', label: 'ETF Global Aggregate Bond', ret: 2,    risk: 0 },
  { group: 'Strumenti Italiani',   id: 'conto',      label: 'Conto Deposito',            ret: 2,    risk: 0 },
  { group: 'Strumenti Italiani',   id: 'bfp',        label: 'Buoni Fruttiferi Postali',  ret: 2.5,  risk: 0 },
  { group: 'Strumenti Italiani',   id: 'fondo_cat',  label: 'Fondo Pensione categoria',  ret: 3.5,  risk: 0 },
  { group: 'Materie Prime',        id: 'oro',        label: 'Oro',                       ret: 5,    risk: 0 },
  { group: 'Materie Prime',        id: 'argento',    label: 'Argento ⚠',                ret: 4,    risk: 1 },
  { group: 'Crypto (alto rischio)', id: 'bitcoin',   label: 'Bitcoin ⚠⚠',              ret: 40,   risk: 2 },
  { group: 'Crypto (alto rischio)', id: 'ethereum',  label: 'Ethereum ⚠⚠',             ret: 35,   risk: 2 },
  { group: 'Portafogli Predefiniti', id: 'lazy60',   label: 'Portafoglio Pigro 60/40',   ret: 5.5,  risk: 0, tooltip: '60% ETF MSCI World + 40% ETF obbligazionario globale' },
  { group: 'Portafogli Predefiniti', id: 'permanent', label: 'Portafoglio Permanente',   ret: 4,    risk: 0, tooltip: '25% azioni + 25% oro + 25% bond + 25% liquidità' },
  { group: 'Portafogli Predefiniti', id: 'allweather', label: 'All Weather Ray Dalio',   ret: 5,    risk: 0, tooltip: '30% azioni + 55% bond + 15% oro. Progettato per resistere a qualsiasi scenario economico' },
];
export const STRUMENTI_MAP = Object.fromEntries(STRUMENTI_DATA.map(s => [s.id, s]));
export const STRUMENTI_GROUPS = [...new Set(STRUMENTI_DATA.map(s => s.group))];

// ── Educational texts ──
export const EDUC = {
  pac: 'Il PAC — Piano di Accumulo del Capitale — consiste nell\'investire una somma fissa ogni mese nel tempo. Grazie all\'interesse composto, anche piccoli versamenti mensili diventano un capitale significativo in 10, 20 o 30 anni.',
  budget: 'Il budget mensile è la base di ogni salute finanziaria. Conoscere entrate e uscite permette di identificare sprechi e aumentare il risparmio. Regola 50/30/20: 50% bisogni, 30% desideri, 20% risparmio.',
  emergency: 'Il fondo di emergenza è una riserva liquida che copre spese impreviste senza toccare gli investimenti. Regola: 3-6 mesi di spese essenziali in un conto deposito separato.',
  savings: 'Calcola in quanto tempo raggiungerai un obiettivo finanziario specifico — vacanza, auto, anticipo casa — combinando risparmio mensile e rendimento degli interessi.',
  mutuo: 'Il mutuo è probabilmente il debito più grande della tua vita. Questo calcolatore mostra rata mensile, costo totale reale e tutti i costi nascosti che le banche raramente comunicano chiaramente.',
  stipendio: 'Trasforma la tua RAL lorda in stipendio mensile reale, mostrando quanto va a IRPEF, quanto ai contributi INPS e cosa rimane effettivamente in tasca ogni mese.',
  inflazione: 'L\'inflazione riduce il potere d\'acquisto ogni anno silenziosamente. Con inflazione al 2,5%, €100.000 oggi equivalgono a €60.000 reali tra 20 anni.',
  fire: 'FIRE (Financial Independence, Retire Early) si basa sulla regola del 4%: se il patrimonio è 25 volte le spese annuali, puoi vivere di rendita indefinitamente. Scopri quando puoi raggiungere la libertà finanziaria.',
  pacAvanzato: 'Simula la crescita reale degli investimenti su strumenti specifici — ETF globali, crypto, portafogli predefiniti — usando rendimenti storici reali. Confronta fino a 3 strumenti sullo stesso grafico.',
  pensione: 'Per chi lavora dal 1996 la pensione pubblica sarà inferiore alla generazione precedente. Il fondo pensione colma questo gap con un bonus: contributi deducibili fino a €5.164/anno.',
  fondi: 'Le commissioni annue (TER) sembrano piccole ma su 20-30 anni erodono decine di migliaia di euro. Un fondo attivo TER 1,5% può costare 4 volte un ETF passivo TER 0,2% a parità di rendimento.',
  surroga: 'La surroga trasferisce il mutuo a un\'altra banca a condizioni migliori senza costi per te (legge 40/2007). Calcola il break-even point e scopri se conviene nel tuo caso specifico.',
  affitto: 'Comprare o affittare? È la domanda finanziaria più importante della vita per molti italiani. Questo calcolatore confronta il costo totale reale dei due scenari su un orizzonte temporale scelto, includendo tutti i costi spesso dimenticati: manutenzione, IMU, rivalutazione immobiliare e adeguamento ISTAT degli affitti.',
  tfr: 'Dal 1° luglio 2026 il TFR dei neoassunti nel settore privato viene automaticamente destinato al fondo pensione di categoria se il lavoratore non esprime una scelta diversa entro 6 mesi dall\'assunzione. Questo calcolatore ti mostra quanto vale il tuo TFR e cosa succede lasciandolo in azienda versus trasferendolo al fondo pensione.',
  isee: 'L\'ISEE determina l\'accesso a decine di bonus e agevolazioni pubbliche: bonus asilo nido, rette universitarie, bonus bollette e molto altro. Questo simulatore stima la tua fascia ISEE e le agevolazioni potenziali — non sostituisce la dichiarazione ufficiale DSU presso un CAF.',
  detrazioni: 'La dichiarazione dei redditi non è solo un obbligo — è un\'opportunità. Ogni anno puoi recuperare parte delle tasse pagate grazie a detrazioni su spese mediche, interessi del mutuo, figli a carico, ristrutturazioni e molto altro. Scopri quanto puoi recuperare.',
  leasing: 'Il leasing sembra conveniente per le rate basse, ma il costo totale su 4-5 anni può essere significativamente più alto di un acquisto. Questo calcolatore confronta i tre scenari reali: acquisto cash, finanziamento bancario e leasing — mostrando il costo netto reale per ciascuno.',
};

// ── Number formatter ──
export const fmt = (n) => Math.round(n).toLocaleString('it-IT');

// ── Chart helpers ──
export const gridColor = 'rgba(255,255,255,0.06)';
export const baseChartOpts = (title) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: { display: !!title, text: title, color: '#888780', font: { size: 13 } },
    tooltip: {
      backgroundColor: '#252523',
      titleColor: '#E8E8E4',
      bodyColor: '#888780',
      borderColor: 'rgba(255,255,255,0.1)',
      borderWidth: 1,
      callbacks: {
        label: (ctx) => `€${fmt(ctx.parsed.y ?? ctx.parsed)}`,
      },
    },
  },
  scales: {
    x: {
      ticks: { color: '#888780', font: { size: 12 } },
      grid: { color: gridColor },
    },
    y: {
      ticks: { color: '#888780', font: { size: 13 }, callback: (v) => `€${fmt(v)}` },
      grid: { color: gridColor },
    },
  },
});

// Bar label plugin
export const barLabelPlugin = {
  id: 'barLabel',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      meta.data.forEach((bar, i) => {
        const v = ds.data[i];
        if (v == null) return;
        ctx.save();
        ctx.font = 'bold 11px Inter, sans-serif';
        ctx.fillStyle = '#E8E8E4';
        ctx.textAlign = 'center';
        ctx.fillText(`€${fmt(v)}`, bar.x, bar.y - 6);
        ctx.restore();
      });
    });
  },
};

// Toast dedup helper
export const showToastOnce = (key, fn) => {
  if (sessionStorage.getItem(`sap_toast_${key}`)) return;
  sessionStorage.setItem(`sap_toast_${key}`, '1');
  fn();
};

// IRPEF calculation (2026 brackets)
export const calcIRPEF = (reddito) => {
  if (reddito <= 28000) return reddito * 0.23;
  if (reddito <= 50000) return 6440 + (reddito - 28000) * 0.33;
  return 13700 + (reddito - 50000) * 0.43;
};

// ── Shared UI components ──
// Deployment 15 — P1: use the unified .sapiens-note-box class so every calculator
// shares the EXACT same intro-box style as the original FondoEmergenza.
export const EducBox = ({ text, children }) => (
  <div className="sapiens-note-box mb-5 text-sm" data-testid="educ-box">
    {text || children}
  </div>
);

export const ChartBox = ({ children, title, height = 420 }) => (
  <div className="mt-6">
    {title && <p className="text-sm text-[#888780] mb-3">{title}</p>}
    <div style={{ height, minHeight: 280 }}>{children}</div>
  </div>
);

export const ResultCard = ({ label, value, green, gold, sub }) => (
  <div className="p-4 bg-[#252523] rounded-xl border border-white/[0.06]">
    <p className="text-xs text-[#888780] mb-1">{label}</p>
    <p className={`text-xl font-semibold ${green ? 'text-[#3D7A52]' : gold ? 'text-[#C9A84C]' : 'text-[#E8E8E4]'}`}>{value}</p>
    {sub && <p className="text-xs text-[#888780] mt-1">{sub}</p>}
  </div>
);

// ── Number stepper (Deployment 14 — replaces native HTML number arrows) ──
// Touch target 44×44px, ARIA labels, parametric step/min/max.
// Used by InputRow on all 33 calculators.
const NumberStepperButton = ({ direction, onClick, disabled }) => (
  <button
    type="button"
    tabIndex={-1}
    aria-label={direction === 'up' ? 'Aumenta valore' : 'Diminuisci valore'}
    onClick={onClick}
    disabled={disabled}
    style={{
      width: 32, height: 32, minWidth: 32,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#252523',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 6,
      color: '#E8E8E4',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      flexShrink: 0,
      lineHeight: 1,
      fontSize: 16,
      fontWeight: 600,
      padding: 0,
      transition: 'background 0.15s',
    }}
    data-testid={direction === 'up' ? 'stepper-up' : 'stepper-down'}
    onMouseOver={(e) => { if (!disabled) e.currentTarget.style.background = '#2F2F2D'; }}
    onMouseOut={(e) => { e.currentTarget.style.background = '#252523'; }}
  >
    {direction === 'up' ? '+' : '−'}
  </button>
);

export const InputRow = ({ label, value, onChange, prefix, suffix, type = 'number', min, max, step, tooltip }) => {
  // Default stepper params if caller didn't pass min/max/step (BUG 5 fallback by category):
  const numStep = step != null ? Number(step) : 1;
  const numMin = min != null ? Number(min) : -Infinity;
  const numMax = max != null ? Number(max) : Infinity;
  const isNumeric = type === 'number';

  const clamp = (v) => Math.min(numMax, Math.max(numMin, v));
  const stepBy = (delta) => {
    const n = Number(value) || 0;
    const next = clamp(Math.round((n + delta) * 1e6) / 1e6);
    onChange(String(next));
  };

  return (
    <div>
      <Label className="text-[#888780] text-sm flex items-center gap-1">
        {label}
        {tooltip && <span title={tooltip} className="cursor-help opacity-50"><Info className="w-3 h-3" /></span>}
      </Label>
      <div className="mt-1 flex items-stretch gap-1.5">
        {isNumeric && (
          <NumberStepperButton direction="down" onClick={() => stepBy(-numStep)} disabled={Number(value) <= numMin} />
        )}
        <div className="relative flex-1">
          {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm pointer-events-none">{prefix}</span>}
          <Input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            min={min} max={max} step={step}
            className={`bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] sapiens-no-spinner ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-10' : ''}`}
          />
          {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm pointer-events-none">{suffix}</span>}
        </div>
        {isNumeric && (
          <NumberStepperButton direction="up" onClick={() => stepBy(numStep)} disabled={Number(value) >= numMax} />
        )}
      </div>
    </div>
  );
};

export const PremiumLock = () => {
  const navigate = useNavigate();
  // Respect admin preview mode
  const isPreview = sessionStorage.getItem('admin_preview_premium') === 'true';
  if (isPreview) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(28,28,26,0.92)', backdropFilter: 'blur(8px)', borderRadius: 'inherit', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Lock style={{ width: 24, height: 24, color: '#C9A84C' }} />
      </div>
      <h3 style={{ color: '#E8E8E4', fontSize: 18, fontWeight: 600, margin: 0 }}>Funzionalità Premium</h3>
      <p style={{ color: '#888780', fontSize: 14, textAlign: 'center', maxWidth: 260, lineHeight: 1.6 }}>Passa a Premium per sbloccare tutti i calcolatori avanzati</p>
      <button
        onClick={() => navigate('/premium')}
        className="sapiens-btn-primary px-6 py-2.5 text-sm"
        data-testid="premium-lock-btn"
      >
        Sblocca con Premium
      </button>
    </div>
  );
};

export const StrumentoSelect = ({ value, onChange, testId, placeholder }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm"
    data-testid={testId}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {STRUMENTI_GROUPS.map(grp => (
      <optgroup key={grp} label={grp}>
        {STRUMENTI_DATA.filter(s => s.group === grp).map(s => (
          <option key={s.id} value={s.id}>{s.label} — {s.ret}%/anno storico</option>
        ))}
      </optgroup>
    ))}
  </select>
);

export const RiskWarning = ({ strumentoId }) => {
  const s = STRUMENTI_MAP[strumentoId];
  if (!s || s.risk === 0) return null;
  if (s.risk === 1) return (
    <div className="p-3 rounded-xl text-sm mt-3" style={{ background: 'rgba(120,80,0,0.15)', border: '1px solid rgba(180,120,0,0.35)', color: '#F0D080' }}>
      ⚠ Strumento ad alta volatilità. I rendimenti storici possono differire significativamente in futuro.
    </div>
  );
  return (
    <div className="p-3 rounded-xl text-sm mt-3" style={{ background: 'rgba(120,30,30,0.2)', border: '1px solid rgba(180,60,60,0.4)', color: '#F08080' }}>
      ⚠⚠ Volatilità estrema. La perdita parziale o totale del capitale è possibile. I rendimenti storici passati non si ripeteranno necessariamente.
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────
// Deployment 26 — Sezione 4 (Legal updates v2.1 — Maggio 2026)
// Disclaimer MiFID II uniforme su tutti i calcolatori SAPIENS.
// Prop `showLegalUpdate`: se true, aggiunge la riga "Aggiornato a Maggio 2026"
// per i calcolatori soggetti a variazioni normative (successione, crypto,
// pensione complementare).
// Stile coerente con i box di avviso arancioni già usati nel sito.
// ─────────────────────────────────────────────────────────────────────
export const LegalDisclaimer = ({ showLegalUpdate = false }) => (
  <div
    className="mt-6 p-4 rounded-xl text-xs leading-relaxed"
    style={{
      background: 'rgba(232,144,58,0.08)',
      border: '1px solid rgba(232,144,58,0.25)',
      color: '#E8E8E4',
    }}
    data-testid="legal-disclaimer-mifid"
  >
    <p className="font-semibold text-[#E8903A] mb-2">⚠️ Nota legale</p>
    <p className="text-[#888780]">
      I contenuti e i calcolatori di SAPIENS hanno esclusivamente finalità educativa e informativa generale.
      Non costituiscono consulenza finanziaria, fiscale, legale o di investimento ai sensi del{' '}
      <strong className="text-[#E8E8E4]">D.Lgs. 58/1998 (TUF)</strong> e della{' '}
      <strong className="text-[#E8E8E4]">Direttiva MiFID II (2014/65/UE)</strong>.
      I risultati sono stime matematiche basate sui parametri inseriti e non tengono conto della situazione
      personale dell'utente. Prima di prendere decisioni finanziarie consulta un professionista abilitato.
    </p>
    {showLegalUpdate && (
      <p className="text-[#888780] mt-2" data-testid="legal-disclaimer-update">
        <strong className="text-[#E8E8E4]">Aggiornato a:</strong> Maggio 2026 — La normativa è soggetta a
        variazioni. Verifica sempre:{' '}
        <a href="https://www.agenziaentrate.gov.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">
          agenziaentrate.gov.it
        </a>
        ,{' '}
        <a href="https://www.inps.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">
          inps.it
        </a>
        .
      </p>
    )}
  </div>
);
